import React, { useState, useMemo } from "react";
import { ModalCompAdvance } from "../../../../components";
import { FiGrid } from "react-icons/fi";
import CategoryPanel from "../../../../components/MemberProfile/CategoryPanel";
import { convertToTitleCase } from "../../../../utils/helpers";
import ContributionEntriesModal from "../ContributionEntriesModal";
import { useGetMemberContributionHistoryQuery } from "../../../../store/member/member-contribution";
import TableSkeletonLoader from "../../../../components/Common/TableSkeletonLoader";

const ContributionsTab = ({ contributions = [], total = 0, memberId = null, isMemberContext = false }) => {
  const [selectedCategory, setSelectedCategory] = useState(null);

  // Fetch contribution history if breakdown is empty but total > 0 (for member context)
  // ContributionsTab should only show NON-WITHDRAWABLE contributions
  const shouldFetchHistory = isMemberContext && (!contributions || contributions.length === 0) && total > 0;
  
  const {
    data: contributionHistoryData,
    isLoading: isLoadingHistory,
    isFetching: isFetchingHistory,
  } = useGetMemberContributionHistoryQuery(
    {
      page: 1,
      limit: 1000, // Fetch all contributions to group them
      filter_type: "other", // Other non-savings, non-shares categories
    },
    { skip: !shouldFetchHistory }
  );

  // Group contributions by category
  const groupedContributions = useMemo(() => {
    // If contributions are already provided, use them
    if (contributions && contributions.length > 0) {
      return contributions;
    }

    // If we have history data, group it by category
    // The API returns { contributions: [...], pagination: {...} }
    const contributionsList = contributionHistoryData?.contributions || contributionHistoryData?.histories || [];
    
    // Debug: Log to see what we're getting
    if (process.env.NODE_ENV === 'development' && contributionHistoryData) {
      console.log('ContributionsTab - contributionHistoryData:', contributionHistoryData);
      console.log('ContributionsTab - contributionsList:', contributionsList);
    }
    
    if (contributionsList && contributionsList.length > 0) {
      const grouped = {};
      
      contributionsList.forEach((contribution) => {
        // Get category details for threshold and withdrawable status
        const category = contribution?.contribution_category || contribution?.category || {};
        
        // Contributions tab = "other" only (savings → Savings, shares → Shares)
        const kind = (category.category_kind || "").toString().toLowerCase().trim();
        if (kind === "savings" || kind === "shares") return;
        if (!kind && Number(category.is_withdrawable) === 1) return;

        // Get category ID - try multiple possible property paths
        const categoryId = contribution?.contribution_category_id || 
                          contribution?.contribution_category?.id ||
                          contribution?.category_id ||
                          contribution?.category?.id ||
                          'unknown';
        
        // Get category name - try multiple possible property paths
        const categoryName = contribution?.contribution_category?.name ||
                            contribution?.category?.name ||
                            contribution?.contribution_category_name ||
                            contribution?.category_name ||
                            'Unknown Category';

        if (!grouped[categoryId]) {
          grouped[categoryId] = {
            category_id: categoryId,
            category_name: categoryName,
            total_amount: 0,
            amount_remaining: null,
            threshold: category?.threshold || null,
            is_withdrawable: 0, // Contributions are always non-withdrawable
            entries: [],
          };
        }

        grouped[categoryId].total_amount += parseFloat(contribution?.amount || 0);
        
        // Build payment method name (could be object or string)
        let paymentMethodName = contribution?.payment_method;
        if (typeof paymentMethodName === 'object' && paymentMethodName?.name) {
          paymentMethodName = paymentMethodName.name;
        } else if (typeof paymentMethodName === 'string') {
          paymentMethodName = paymentMethodName.replace(/_/g, ' ');
        }

        grouped[categoryId].entries.push({
          id: contribution?.id,
          amount: contribution?.amount,
          payment_date: contribution?.payment_date || contribution?.created_at,
          payment_method: paymentMethodName,
          status: contribution?.status,
          reference: contribution?.reference,
        });
      });

      return Object.values(grouped);
    }

    return [];
  }, [contributions, contributionHistoryData]);

  const handleOpen = (category) => {
    setSelectedCategory({ ...category, type: "contribution" });
  };

  const handleClose = () => setSelectedCategory(null);

  if (isLoadingHistory || isFetchingHistory) {
    return (
      <div className="py-5" aria-busy="true">
        <TableSkeletonLoader rows={3} columns={2} />
      </div>
    );
  }

  return (
    <div className="w-full">
      <CategoryPanel
        groups={groupedContributions || []}
        total={total}
        title="Other contributions"
        tone="blue"
        icon={FiGrid}
        summary="Excludes savings and shares"
        emptyTitle="No contributions recorded yet"
        emptyText="Contribution payments for this member will show up here."
        missingTitle="Couldn't load the contributions breakdown"
        onOpen={handleOpen}
        onRetry={() => window.location.reload()}
      />

      <ModalCompAdvance
        show={!!selectedCategory}
        handleClose={handleClose}
        close
        size="lg"
        centered
        modalBody={0}
        arrayComp={[
          {
            component:
              selectedCategory && (
                <ContributionEntriesModal
                  type="contribution"
                  categoryName={convertToTitleCase(
                    selectedCategory.category_name || "Unknown Category"
                  )}
                  total={selectedCategory.total_amount || 0}
                  entries={selectedCategory.entries || []}
                  onClose={handleClose}
                />
              ),
          },
        ]}
      />
    </div>
  );
};

export default ContributionsTab;
